/**
 * Comprehensive Floor Plan Diagnostics Tool
 * 
 * This tool provides deep analysis of floor plan state integrity issues
 * that occur in complex multi-floor scenarios with extrusions and splits.
 */

import type { Wall, Room, WallWithTempId } from '../types';
import { analyzeExtrusionRelationships, ExtrusionRelationship } from './extrusionMetadataPreservation';

export interface DiagnosticResult {
  severity: 'info' | 'warning' | 'error' | 'critical';
  category: 'extrusion' | 'walls' | 'rooms' | 'labels' | 'geometry';
  issue: string;
  details: any;
  suggestedFix?: string;
}

export interface FloorPlanDiagnostics {
  floorNumber: number;
  totalIssues: number;
  criticalIssues: number;
  results: DiagnosticResult[];
  summary: {
    walls: number;
    rooms: number;
    extrusions: number;
    connectors: number;
    orphanedWalls: number;
    duplicateConnectors: number;
    roomsWithMissingWalls: number;
  };
}

/**
 * Comprehensive diagnostic analysis of floor plan state
 */
export function diagnoseFloorPlan(
  walls: WallWithTempId[],
  rooms: Room[],
  roomLabels: Record<string, string>,
  floorNumber: number
): FloorPlanDiagnostics {
  const results: DiagnosticResult[] = [];
  
  // 1. EXTRUSION INTEGRITY ANALYSIS
  analyzeExtrusionIntegrity(walls, results);
  
  // 2. WALL DUPLICATE DETECTION
  analyzeWallDuplicates(walls, results);
  
  // 3. ROOM-WALL MEMBERSHIP VALIDATION
  analyzeRoomWallMembership(walls, rooms, results);
  
  // 4. GEOMETRY CONSISTENCY CHECK
  analyzeGeometryConsistency(walls, results);
  
  // 5. ROOM LABEL INTEGRITY
  analyzeRoomLabelIntegrity(rooms, roomLabels, results);
  
  // 6. WALL IDENTITY TRACKING
  analyzeWallIdentityIntegrity(walls, results);
  
  const summary = generateSummary(walls, rooms, results);
  const criticalIssues = results.filter(r => r.severity === 'critical').length;
  
  // 🔍 ENHANCED DIAGNOSTIC BREAKDOWN - Log detailed issue categorization
  const issueBreakdown = {
    byCategory: results.reduce((acc, r) => { acc[r.category] = (acc[r.category] || 0) + 1; return acc; }, {} as Record<string, number>),
    bySeverity: results.reduce((acc, r) => { acc[r.severity] = (acc[r.severity] || 0) + 1; return acc; }, {} as Record<string, number>),
    byIssueType: results.reduce((acc, r) => { acc[r.issue] = (acc[r.issue] || 0) + 1; return acc; }, {} as Record<string, number>)
  };
  
  console.log(`🔍 DIAGNOSTIC_BREAKDOWN_FLOOR_${floorNumber}`, {
    totalIssues: results.length,
    criticalIssues,
    issueBreakdown
  });
  
  // 🔍 LOG DETAILED ISSUE TYPES for analysis
  console.log(`📊 DETAILED_ISSUES_FLOOR_${floorNumber}:`, results.map(r => `${r.severity}|${r.category}|${r.issue}`));
  
  // 🔍 LOG SPECIFIC ISSUE SAMPLES for debugging
  Object.entries(issueBreakdown.byIssueType).forEach(([issueType, count]) => {
    const sample = results.find(r => r.issue === issueType);
    if (sample) {
      console.log(`🔍 ISSUE_SAMPLE_${floorNumber}_${issueType.replace(/\s+/g, '_').toUpperCase()}`, {
        issue: sample.issue,
        severity: sample.severity,
        category: sample.category,
        count,
        sampleDetails: sample.details,
        suggestedFix: sample.suggestedFix
      });
    }
  });
  
  return {
    floorNumber,
    totalIssues: results.length,
    criticalIssues,
    results,
    summary
  };
}

/**
 * 1. EXTRUSION INTEGRITY ANALYSIS
 * Detects extrusion-related issues that prevent resizing
 * Uses same validation logic as repair system for consistency
 */
function analyzeExtrusionIntegrity(walls: WallWithTempId[], results: DiagnosticResult[]): void {
  // Use the EXACT same validation function as repair system for consistency
  const relationships = analyzeExtrusionRelationships(walls);
  
  console.log('🔍 DIAGNOSTICS_VALIDATION_CHECK', {
    totalRelationships: relationships.length,
    validRelationships: relationships.filter(r => r.isValid).length,
    invalidRelationships: relationships.filter(r => !r.isValid).length,
    relationshipDetails: relationships.map(r => ({ mainSegmentId: r.mainSegmentId, isValid: r.isValid, connectorCount: r.connectorIds.length }))
  });
  
  relationships.forEach(relationship => {
    if (!relationship.isValid) {
      console.log('🚨 INVALID_RELATIONSHIP_FOUND', {
        mainSegmentId: relationship.mainSegmentId,
        isValid: relationship.isValid,
        connectorCount: relationship.connectorIds.length,
        connectorIds: relationship.connectorIds
      });
      // Enhanced debugging for remaining corruption cases
      const mainWall = walls.find(w => (w as any).tempId === relationship.mainSegmentId);
      const allConnectors = walls.filter(w => (w as any).isExtrusionConnector);
      const nearbyConnectors = allConnectors.filter(c => {
        if (!mainWall) return false;
        const distance = Math.sqrt(
          Math.pow(c.start.x - mainWall.start.x, 2) + Math.pow(c.start.y - mainWall.start.y, 2)
        ) + Math.sqrt(
          Math.pow(c.start.x - mainWall.end.x, 2) + Math.pow(c.start.y - mainWall.end.y, 2)
        );
        return distance < 100; // Within 100px
      });
      
      console.log('🚨 CORRUPTION_CASE_ANALYSIS', {
        mainSegmentId: relationship.mainSegmentId,
        mainSegmentCoords: mainWall ? `${mainWall.start.x},${mainWall.start.y} to ${mainWall.end.x},${mainWall.end.y}` : 'NOT_FOUND',
        mainSegmentParentWallId: mainWall ? (mainWall as any).parentWallId : 'N/A',
        connectorCount: relationship.connectorIds.length,
        connectorIds: relationship.connectorIds,
        nearbyConnectors: nearbyConnectors.map(c => ({
          tempId: (c as any).tempId,
          mainSegmentId: (c as any).mainSegmentId,
          parentWallId: (c as any).parentWallId,
          coords: `${c.start.x},${c.start.y} to ${c.end.x},${c.end.y}`,
          distance: mainWall ? Math.min(
            Math.sqrt(Math.pow(c.start.x - mainWall.start.x, 2) + Math.pow(c.start.y - mainWall.start.y, 2)),
            Math.sqrt(Math.pow(c.start.x - mainWall.end.x, 2) + Math.pow(c.start.y - mainWall.end.y, 2))
          ).toFixed(1) + 'px' : 'N/A'
        })),
        corruptionType: relationship.connectorIds.length === 0 ? 'NO_CONNECTORS' : 'INSUFFICIENT_CONNECTORS'
      });
      
      results.push({
        severity: 'critical',
        category: 'extrusion',
        issue: 'Extrusion metadata corruption',
        details: {
          mainSegmentId: relationship.mainSegmentId,
          connectorCount: relationship.connectorIds.length,
          connectorIds: relationship.connectorIds,
          parentWallId: relationship.parentWallId
        },
        suggestedFix: 'Run extrusion metadata repair system to restore connector relationships'
      });
    }
  });
  
  // Orphaned connector validation is now handled by the unified analyzeExtrusionRelationships() function above
  // This ensures consistent validation logic between diagnostics and repair systems
}

// Removed duplicate validation function - now using analyzeExtrusionRelationships() from extrusionMetadataPreservation.ts

/**
 * 2. WALL DUPLICATE DETECTION
 * Finds duplicate walls that cause rendering and calculation issues
 */
function analyzeWallDuplicates(walls: WallWithTempId[], results: DiagnosticResult[]): void {
  const tolerance = 5.0; // 5px tolerance for coordinate matching
  
  for (let i = 0; i < walls.length; i++) {
    for (let j = i + 1; j < walls.length; j++) {
      const wall1 = walls[i];
      const wall2 = walls[j];
      
      // Check for coordinate overlap (potential duplicates)
      const sameStart = Math.abs(wall1.start.x - wall2.start.x) < tolerance && 
                       Math.abs(wall1.start.y - wall2.start.y) < tolerance;
      const sameEnd = Math.abs(wall1.end.x - wall2.end.x) < tolerance && 
                     Math.abs(wall1.end.y - wall2.end.y) < tolerance;
      
      const reversedStart = Math.abs(wall1.start.x - wall2.end.x) < tolerance && 
                           Math.abs(wall1.start.y - wall2.end.y) < tolerance;
      const reversedEnd = Math.abs(wall1.end.x - wall2.start.x) < tolerance && 
                         Math.abs(wall1.end.y - wall2.start.y) < tolerance;
      
      if ((sameStart && sameEnd) || (reversedStart && reversedEnd)) {
        const bothConnectors = (wall1 as any).isExtrusionConnector && (wall2 as any).isExtrusionConnector;
        
        results.push({
          severity: bothConnectors ? 'critical' : 'warning',
          category: 'walls',
          issue: bothConnectors ? 'Duplicate connector walls detected' : 'Duplicate walls detected',
          details: {
            wall1Id: wall1.tempId,
            wall2Id: wall2.tempId,
            wall1Coords: `${wall1.start.x},${wall1.start.y} to ${wall1.end.x},${wall1.end.y}`,
            wall2Coords: `${wall2.start.x},${wall2.start.y} to ${wall2.end.x},${wall2.end.y}`,
            wall1IsConnector: (wall1 as any).isExtrusionConnector,
            wall2IsConnector: (wall2 as any).isExtrusionConnector
          },
          suggestedFix: 'Remove one of the duplicate walls'
        });
      }
    }
  }
}

/**
 * 3. ROOM-WALL MEMBERSHIP VALIDATION
 * Ensures rooms maintain proper wall associations
 */
function analyzeRoomWallMembership(walls: WallWithTempId[], rooms: Room[], results: DiagnosticResult[]): void {
  console.log('🔍 ROOM_WALL_MEMBERSHIP_ANALYSIS_START', {
    wallCount: walls.length,
    roomCount: rooms.length,
    wallsWithTempId: walls.filter(w => w.tempId).length,
    connectorWalls: walls.filter(w => (w as any).isExtrusionConnector).length
  });
  rooms.forEach(room => {
    // Check if room walls still exist in the wall collection
    const missingWalls: string[] = [];
    room.walls.forEach(roomWall => {
      const wallExists = walls.some(w => 
        w.tempId === (roomWall as any).tempId ||
        (Math.abs(w.start.x - roomWall.start.x) < 1 && 
         Math.abs(w.start.y - roomWall.start.y) < 1 &&
         Math.abs(w.end.x - roomWall.end.x) < 1 && 
         Math.abs(w.end.y - roomWall.end.y) < 1)
      );
      
      if (!wallExists) {
        missingWalls.push((roomWall as any).tempId || `${roomWall.start.x},${roomWall.start.y}-${roomWall.end.x},${roomWall.end.y}`);
      }
    });
    
    if (missingWalls.length > 0) {
      results.push({
        severity: 'critical',
        category: 'rooms',
        issue: 'Room references non-existent walls',
        details: {
          roomId: room.stableId || room.id,
          roomName: room.name,
          missingWallIds: missingWalls,
          roomWallCount: room.walls.length
        },
        suggestedFix: 'Recalculate room from existing walls or restore missing walls'
      });
    }
  });
  
  // Check for walls without room membership
  const orphanedWalls = walls.filter(wall => {
    const hasRoomMembership = (wall as any).parentRoomIds && (wall as any).parentRoomIds.length > 0;
    const isInSomeRoom = rooms.some(room => 
      room.walls.some(roomWall => 
        (roomWall as any).tempId === wall.tempId ||
        (Math.abs(roomWall.start.x - wall.start.x) < 1 && 
         Math.abs(roomWall.start.y - wall.start.y) < 1 &&
         Math.abs(roomWall.end.x - wall.end.x) < 1 && 
         Math.abs(roomWall.end.y - wall.end.y) < 1)
      )
    );
    
    return !hasRoomMembership && !isInSomeRoom && !(wall as any).isExtrusionConnector;
  });
  
  console.log('🔍 ORPHANED_WALL_ANALYSIS', {
    totalWalls: walls.length,
    wallsWithParentRoomIds: walls.filter(w => (w as any).parentRoomIds && (w as any).parentRoomIds.length > 0).length,
    connectorWalls: walls.filter(w => (w as any).isExtrusionConnector).length,
    orphanedWallsFound: orphanedWalls.length,
    orphanedWallDetails: orphanedWalls.map(w => ({
      tempId: w.tempId,
      coords: `${w.start.x},${w.start.y} to ${w.end.x},${w.end.y}`,
      hasParentRoomIds: !!(w as any).parentRoomIds,
      isConnector: !!(w as any).isExtrusionConnector
    }))
  });
  
  orphanedWalls.forEach(orphanedWall => {
    results.push({
      severity: 'warning',
      category: 'walls',
      issue: 'Orphaned wall without room membership',
      details: {
        wallId: orphanedWall.tempId,
        coords: `${orphanedWall.start.x},${orphanedWall.start.y} to ${orphanedWall.end.x},${orphanedWall.end.y}`,
        hasParentRoomIds: !!(orphanedWall as any).parentRoomIds,
        isConnector: !!(orphanedWall as any).isExtrusionConnector
      },
      suggestedFix: 'Assign wall to appropriate room or remove if unnecessary'
    });
  });
}

/**
 * 4. GEOMETRY CONSISTENCY CHECK
 * Validates coordinate precision and wall connections
 */
function analyzeGeometryConsistency(walls: WallWithTempId[], results: DiagnosticResult[]): void {
  const tolerance = 0.1; // Very tight tolerance for precision issues
  
  walls.forEach(wall => {
    // Check for zero-length walls
    const length = Math.sqrt(
      Math.pow(wall.end.x - wall.start.x, 2) + 
      Math.pow(wall.end.y - wall.start.y, 2)
    );
    
    if (length < tolerance) {
      results.push({
        severity: 'error',
        category: 'geometry',
        issue: 'Zero-length wall detected',
        details: {
          wallId: wall.tempId,
          start: wall.start,
          end: wall.end,
          length: length
        },
        suggestedFix: 'Remove zero-length wall'
      });
    }
    
    // Check for significant floating-point precision issues using tolerance-based approach
    // This ignores micro-drift from JSON serialization but catches actual precision problems
    // Only reports coordinates that are significantly off-grid (more than 0.01mm from nearest grid point)
    const TOLERANCE = 0.01; // 0.01mm tolerance for micro-precision errors from JSON serialization
    const hasFloatingPointDrift = 
      (Math.abs(wall.start.x % 0.1) > TOLERANCE && Math.abs(wall.start.x % 0.1) < (0.1 - TOLERANCE)) ||
      (Math.abs(wall.start.y % 0.1) > TOLERANCE && Math.abs(wall.start.y % 0.1) < (0.1 - TOLERANCE)) ||
      (Math.abs(wall.end.x % 0.1) > TOLERANCE && Math.abs(wall.end.x % 0.1) < (0.1 - TOLERANCE)) ||
      (Math.abs(wall.end.y % 0.1) > TOLERANCE && Math.abs(wall.end.y % 0.1) < (0.1 - TOLERANCE));
    
    if (hasFloatingPointDrift) {
      results.push({
        severity: 'info',
        category: 'geometry',
        issue: 'Significant coordinate precision drift detected',
        details: {
          wallId: wall.tempId,
          start: wall.start,
          end: wall.end
        },
        suggestedFix: 'Snap coordinates to grid'
      });
    }
  });
}

/**
 * 5. ROOM LABEL INTEGRITY
 * Validates room label consistency across floors
 */
function analyzeRoomLabelIntegrity(rooms: Room[], roomLabels: Record<string, string>, results: DiagnosticResult[]): void {
  console.log('🔍 ROOM_LABEL_ANALYSIS_START', {
    roomCount: rooms.length,
    customLabelCount: Object.keys(roomLabels).length,
    roomsWithNames: rooms.filter(r => r.name && !r.name.startsWith('Room ')).length
  });
  rooms.forEach(room => {
    const roomId = room.stableId || room.id;
    const hasCustomLabel = roomLabels[roomId];
    
    if (!hasCustomLabel && room.name && !room.name.startsWith('Room ')) {
      results.push({
        severity: 'info',
        category: 'labels',
        issue: 'Room has name but no custom label entry',
        details: {
          roomId: roomId,
          roomName: room.name,
          hasCustomLabel: !!hasCustomLabel
        }
      });
    }
  });
  
  // Check for label entries without corresponding rooms
  Object.keys(roomLabels).forEach(labelKey => {
    const roomExists = rooms.some(r => (r.stableId || r.id) === labelKey);
    if (!roomExists) {
      results.push({
        severity: 'warning',
        category: 'labels',
        issue: 'Custom label without corresponding room',
        details: {
          labelKey: labelKey,
          labelValue: roomLabels[labelKey]
        },
        suggestedFix: 'Remove orphaned label or restore room'
      });
    }
  });
}

/**
 * 6. WALL IDENTITY TRACKING
 * Validates tempId consistency and uniqueness
 */
function analyzeWallIdentityIntegrity(walls: WallWithTempId[], results: DiagnosticResult[]): void {
  const tempIds = walls.map(w => w.tempId).filter(id => id);
  const duplicateIds = tempIds.filter((id, index) => tempIds.indexOf(id) !== index);
  
  if (duplicateIds.length > 0) {
    results.push({
      severity: 'critical',
      category: 'walls',
      issue: 'Duplicate wall tempIds detected',
      details: {
        duplicateIds: [...new Set(duplicateIds)],
        count: duplicateIds.length
      },
      suggestedFix: 'Regenerate unique tempIds for duplicate walls'
    });
  }
  
  const wallsWithoutTempId = walls.filter(w => !w.tempId);
  if (wallsWithoutTempId.length > 0) {
    results.push({
      severity: 'error',
      category: 'walls',
      issue: 'Walls without tempId',
      details: {
        count: wallsWithoutTempId.length,
        affectedWalls: wallsWithoutTempId.map(w => ({
          start: w.start,
          end: w.end
        }))
      },
      suggestedFix: 'Assign tempIds to all walls'
    });
  }
}

/**
 * Generate summary statistics
 */
function generateSummary(walls: WallWithTempId[], rooms: Room[], results: DiagnosticResult[]) {
  return {
    walls: walls.length,
    rooms: rooms.length,
    extrusions: walls.filter(w => (w as any).isMainExtrusionSegment).length,
    connectors: walls.filter(w => (w as any).isExtrusionConnector).length,
    orphanedWalls: results.filter(r => r.issue.includes('Orphaned')).length,
    duplicateConnectors: results.filter(r => r.issue.includes('Duplicate connector')).length,
    roomsWithMissingWalls: results.filter(r => r.issue.includes('references non-existent')).length
  };
}

/**
 * Generate diagnostic report as formatted string
 */
export function generateDiagnosticReport(diagnostics: FloorPlanDiagnostics): string {
  const { results, summary, floorNumber, criticalIssues } = diagnostics;
  
  let report = `\n🔍 FLOOR PLAN DIAGNOSTICS - Floor ${floorNumber}\n`;
  report += `${'='.repeat(50)}\n\n`;
  
  report += `📊 SUMMARY:\n`;
  report += `  • Walls: ${summary.walls} | Rooms: ${summary.rooms}\n`;
  report += `  • Extrusions: ${summary.extrusions} | Connectors: ${summary.connectors}\n`;
  report += `  • Issues Found: ${results.length} (${criticalIssues} critical)\n\n`;
  
  if (results.length === 0) {
    report += `✅ No issues detected!\n`;
    return report;
  }
  
  const criticalResults = results.filter(r => r.severity === 'critical');
  const errorResults = results.filter(r => r.severity === 'error');
  const warningResults = results.filter(r => r.severity === 'warning');
  
  if (criticalResults.length > 0) {
    report += `🚨 CRITICAL ISSUES (${criticalResults.length}):\n`;
    criticalResults.forEach((result, i) => {
      report += `  ${i + 1}. ${result.issue}\n`;
      report += `     Category: ${result.category}\n`;
      report += `     Details: ${JSON.stringify(result.details, null, 2).replace(/\n/g, '\n     ')}\n`;
      if (result.suggestedFix) {
        report += `     Fix: ${result.suggestedFix}\n`;
      }
      report += `\n`;
    });
  }
  
  if (errorResults.length > 0) {
    report += `❌ ERRORS (${errorResults.length}):\n`;
    errorResults.forEach((result, i) => {
      report += `  ${i + 1}. ${result.issue}\n`;
      if (result.suggestedFix) {
        report += `     Fix: ${result.suggestedFix}\n`;
      }
    });
    report += `\n`;
  }
  
  if (warningResults.length > 0) {
    report += `⚠️  WARNINGS (${warningResults.length}):\n`;
    warningResults.forEach((result, i) => {
      report += `  ${i + 1}. ${result.issue}\n`;
    });
  }
  
  return report;
}
